import { cloneDeep } from 'lodash'

import {
	AGENT_HTML_ELASTIC_COMPONENT_ID,
	ELASTIC_SECTION_COMPONENT_IDS,
	normalizeElasticComponentType,
} from '@/features/websitePageAgentTools'
import { collectAgentSectionI18nFromPageStrings } from '@/utils/websitePageAgentI18n'

/** Max HTML chars per agent-html section in getSnapshot (full vs compact agent context). */
const SNAPSHOT_HTML_PREVIEW_FULL = 4000
const SNAPSHOT_HTML_PREVIEW_COMPACT = 2400
/** In compact mode, first N agent-html sections keep full preview length (merge/add flows need intact markup). */
const SNAPSHOT_COMPACT_FULL_PREVIEW_AGENT_SECTIONS = 2
/** Per-section AI CSS (`page.data[id].agentCss`) preview sizes in getSnapshot. */
const SNAPSHOT_AGENT_CSS_PREVIEW_FULL = 3500
const SNAPSHOT_AGENT_CSS_PREVIEW_COMPACT = 1200
/** Cap chat history chars per message when building agent context (keeps last turn readable). */
export const AGENT_CHAT_HISTORY_CHAR_CAP = 1800

/**
 * @param {object} page - Editor page object (mutable draft)
 * @param {object} vm - EditorWebPage component instance (for methods + socket)
 */
export default function createWebsitePageEditorApi(vm) {
	return {
		/**
		 * @param {{ compact?: boolean }} [options] — compact reduces HTML/CSS preview sizes for lower token use.
		 */
		getSnapshot(options = {}) {
			const compact = options.compact === true
			const p = vm.page || {}
			const data = p.data || {}
			const body = data.body || {}
			const elasticOrder = Array.isArray(body.elastic) ? cloneDeep(body.elastic) : []
			const elasticSections = {}
			let compactAgentHtmlSectionsSeen = 0
			for (let i = 0; i < elasticOrder.length; i += 1) {
				const sid = elasticOrder[i]
				const key = sid != null ? String(sid) : ''
				const entry = key && data[key] && typeof data[key] === 'object' ? data[key] : null
				const idx = key || String(i)
				let sectionEntry
				if (entry) {
					sectionEntry = {
						component: entry.component || null,
						repeat: Array.isArray(entry.repeat) ? [...entry.repeat] : [],
					}
					const comp = entry.component
						? normalizeElasticComponentType(entry.component)
						: ''
					if (comp === AGENT_HTML_ELASTIC_COMPONENT_ID) {
						const h = entry.html != null ? String(entry.html) : ''
						sectionEntry.htmlLength = h.length
						let maxHtmlPreview = SNAPSHOT_HTML_PREVIEW_FULL
						if (compact) {
							maxHtmlPreview = compactAgentHtmlSectionsSeen < SNAPSHOT_COMPACT_FULL_PREVIEW_AGENT_SECTIONS
								? SNAPSHOT_HTML_PREVIEW_FULL
								: SNAPSHOT_HTML_PREVIEW_COMPACT
							compactAgentHtmlSectionsSeen += 1
						}
						if (h.length > maxHtmlPreview) {
							sectionEntry.htmlPreview = `${h.slice(0, maxHtmlPreview)}\n…`
							sectionEntry.htmlPreviewTruncated = true
						} else {
							sectionEntry.htmlPreview = h
						}
						const rawI18n = collectAgentSectionI18nFromPageStrings(
							p.strings,
							vm.locales,
							key,
						) || (
							entry.i18n && typeof entry.i18n === 'object' && !Array.isArray(entry.i18n)
								? entry.i18n
								: null
						)
						if (rawI18n) {
							sectionEntry.i18nFieldIds = Object.keys(rawI18n).filter(k => /^[a-z0-9][a-z0-9-]{0,120}$/i.test(k))
							sectionEntry.i18nLocalesSample = {}
							const firstField = sectionEntry.i18nFieldIds[0]
							if (firstField && rawI18n[firstField]) {
								const locs = rawI18n[firstField]
								Object.keys(locs).slice(0, 5).forEach((lc) => {
									const v = locs[lc] != null ? String(locs[lc]) : ''
									sectionEntry.i18nLocalesSample[lc] = v.length > 200 ? `${v.slice(0, 200)}…` : v
								})
							}
						}
						const imageFieldIds = new Set()
						const reImg = /\bdata-i18n-image\s*=\s*["']([^"']+)["']/gi
						Array.from(h.matchAll(reImg)).forEach((im) => {
							const fid = String(im[1] || '').trim()
							if (/^[a-z0-9][a-z0-9-]{0,120}$/i.test(fid)) {
								imageFieldIds.add(fid)
							}
						})
						if (imageFieldIds.size) {
							sectionEntry.i18nImageFieldIds = [...imageFieldIds]
						}
						const hrefOnlyBases = new Set()
						const reHrefOnly = /\bdata-i18n-href-field\s*=\s*["']([^"']+)["']/gi
						Array.from(h.matchAll(reHrefOnly)).forEach((im) => {
							const fid = String(im[1] || '').trim()
							if (/^[a-z0-9][a-z0-9-]{0,120}$/i.test(fid)) {
								hrefOnlyBases.add(fid)
							}
						})
						if (hrefOnlyBases.size) {
							sectionEntry.i18nAgentHrefOnlyFieldIds = [...hrefOnlyBases]
						}
						const ctaFieldBases = new Set()
						const reCtaAnchor = /<a\b[^>]*\bdata-i18n-cta\b[^>]*>/gi
						Array.from(h.matchAll(reCtaAnchor)).forEach((m) => {
							const chunk = m[0] || ''
							const fm = chunk.match(/\bdata-i18n-field\s*=\s*["']([^"']+)["']/i)
							const fid = fm ? String(fm[1] || '').trim() : ''
							if (/^[a-z0-9][a-z0-9-]{0,120}$/i.test(fid)) {
								ctaFieldBases.add(fid)
							}
						})
						if (ctaFieldBases.size) {
							sectionEntry.i18nAgentCtaFieldIds = [...ctaFieldBases]
						}
						const css = entry.agentCss != null ? String(entry.agentCss) : ''
						sectionEntry.agentCssLength = css.length
						if (css.length) {
							let maxCssPreview = SNAPSHOT_AGENT_CSS_PREVIEW_FULL
							if (compact) {
								maxCssPreview = compactAgentHtmlSectionsSeen <= SNAPSHOT_COMPACT_FULL_PREVIEW_AGENT_SECTIONS
									? SNAPSHOT_AGENT_CSS_PREVIEW_FULL
									: SNAPSHOT_AGENT_CSS_PREVIEW_COMPACT
							}
							if (css.length > maxCssPreview) {
								sectionEntry.agentCssPreview = `${css.slice(0, maxCssPreview)}\n…`
								sectionEntry.agentCssPreviewTruncated = true
							} else {
								sectionEntry.agentCssPreview = css
							}
						}
					}
				} else {
					sectionEntry = { component: null, repeat: [], missingData: true }
				}
				elasticSections[idx] = sectionEntry
			}
			const wtc = vm.websiteThemeColors && typeof vm.websiteThemeColors === 'object'
				? vm.websiteThemeColors
				: {}
			const rawStylesheet = p.stylesheet != null ? String(p.stylesheet) : ''
			return {
				name: p.name,
				layout: p.layout,
				version: p.version,
				/** Always full CSS — truncating mid-rules confused the model; HTML previews are the main token cost. */
				stylesheet: rawStylesheet,
				/** Hub website settings (same source as Website admin); use in generated CSS when relevant. */
				websitePrimaryColor: wtc.primaryColor != null ? String(wtc.primaryColor).trim() : '',
				websiteSecondaryColor: wtc.secondaryColor != null ? String(wtc.secondaryColor).trim() : '',
				elasticContainerSlug: 'body',
				elasticSectionOrder: elasticOrder,
				elasticSections,
				/** Static catalog size only — full list is documented on tools; omits long repeated array in compact mode. */
				elasticSectionComponentCatalogSize: ELASTIC_SECTION_COMPONENT_IDS.length,
				...(compact ? {} : { elasticSectionComponentIds: [...ELASTIC_SECTION_COMPONENT_IDS] }),
				agentHtmlElasticComponentId: AGENT_HTML_ELASTIC_COMPONENT_ID,
				pageOptions: data['page-options'] ? cloneDeep(data['page-options']) : {},
				dataKeys: data && typeof data === 'object' ? Object.keys(data) : [],
				stringsLocales: p.strings && typeof p.strings === 'object' ? Object.keys(p.strings) : [],
				editorSelectedLocale: vm.selectedLang || null,
				websiteLocales: Array.isArray(vm.locales) ? [...vm.locales] : [],
				...(compact ? { snapshotCompact: true } : {}),
			}
		},
		async saveDraftSilent() {
			await vm.onSave(null, true)
		},
		resolveLocaleTargets(locale) {
			const raw = locale == null ? '' : String(locale).trim()
			const allLocales = !raw || raw.toLowerCase() === 'all'

			let targets = []
			if (allLocales) {
				if (Array.isArray(vm.locales) && vm.locales.length) {
					targets = vm.locales.map(l => String(l))
				} else {
					targets = Object.keys(vm.page.strings || {})
				}
				if (targets.length === 0 && vm.selectedLang) {
					targets = [String(vm.selectedLang)]
				}
			} else {
				targets = [raw]
			}

			if (targets.length === 0) {
				targets = vm.selectedLang ? [String(vm.selectedLang)] : ['en']
			}
			return targets
		},
		normalizeCategorySlug(category, slug) {
			const cat = String(category || '').trim()
			let sl = String(slug || '').trim()
			const prefix = cat ? `${cat}.` : ''
			if (prefix && sl.startsWith(prefix)) {
				sl = sl.slice(prefix.length)
			}
			return { cat, sl }
		},
		patchLocaleStrings(entries) {
			const max = 80
			const list = Array.isArray(entries) ? entries.slice(0, max) : []
			const touched = new Set()

			for (let i = 0; i < list.length; i += 1) {
				const e = list[i] || {}
				const normalized = this.normalizeCategorySlug(e.category, e.slug)
				const { cat, sl } = normalized
				if (cat && sl) {
					const targets = this.resolveLocaleTargets(e.locale)
					for (let t = 0; t < targets.length; t += 1) {
						touched.add(targets[t])
						const v = e.value != null ? e.value : ''
						vm.updateText(targets[t], cat, sl, String(v))
					}
				}
			}

			if (vm.socket) {
				vm.socket.emit('action', 'putLocales', vm.page.strings)
			}
			vm.afterUpdate()
			return { locales: [...touched], count: list.length }
		},
		updateLocaleString(locale, category, slug, value) {
			return this.patchLocaleStrings([{
				locale,
				category,
				slug,
				value,
			}])
		},
		runEditContent(payload) {
			vm.editContent(payload)
		},
		/**
		 * @param {{
		 *   stylesheet?: string,
		 *   sections?: Array<object>,
		 *   sectionsReplaceAll?: boolean,
		 *   agentSectionsOrder?: string|string[],
		 * }} payload
		 * @returns {{ ok?: boolean, reason?: string, elasticSectionOrder?: string[], agentSectionIds?: string[] }}
		 */
		syncAgentHtmlLayout(payload) {
			return vm.syncAgentHtmlLayout(payload)
		},
	}
}
